import { apiFetch, apiConfig } from '../config/api';
import { Leadership } from '../types/Leadership';
import AuthService from './AuthService';

export class LeadershipService {
  // Public API methods (no auth required)
  static async getAllActiveLeadership(): Promise<Leadership[]> {
    return await apiFetch('/api/leadership');
  }

  static async getLeadershipById(id: string): Promise<Leadership> {
    return await apiFetch(`/api/leadership/${id}`);
  }

  // Admin API methods (require auth)
  static async getAllLeadership(schoolId?: string): Promise<Leadership[]> {
    const url = schoolId
      ? `/api/admin/leadership?schoolId=${schoolId}`
      : `/api/admin/leadership`;
    return await apiFetch(url);
  }

  static async createLeadership(leadership: Omit<Leadership, 'id' | 'createdAt' | 'updatedAt'>): Promise<Leadership> {
    return await apiFetch('/api/admin/leadership', {
      method: 'POST',
      body: JSON.stringify(leadership),
    });
  }

  static async updateLeadership(id: string, leadership: Partial<Leadership>): Promise<Leadership> {
    return await apiFetch(`/api/admin/leadership/${id}`, {
      method: 'PUT',
      body: JSON.stringify(leadership),
    });
  }

  static async deleteLeadership(id: string): Promise<void> {
    await apiFetch(`/api/admin/leadership/${id}`, {
      method: 'DELETE',
    });
  }

  // Image upload method that needs custom handling for multipart/form-data
  static async uploadLeadershipImage(leadershipId: string, file: File): Promise<Leadership> {
    const formData = new FormData();
    formData.append('file', file);

    // Get token from user object in localStorage (same as apiFetch)
    const storedUser = localStorage.getItem('user');
    let token = null;
    try {
      if (storedUser) {
        const userData = JSON.parse(storedUser);
        token = userData.token;
      }
    } catch (err) {
      console.error('Error parsing user data from localStorage:', err);
    }

    if (!token) {
      throw new Error('Authentication token not found. Please log in.');
    }

    // Use the centralized API configuration instead of hardcoded URL
    const url = `${apiConfig.baseURL}/api/admin/leadership/${leadershipId}/images`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${token}`,
      },
      body: formData,
    });

    if (!response.ok) {
      throw new Error('Failed to upload image');
    }

    return response.json();
  }

  static async deleteLeadershipImage(leadershipId: string, imageId?: string): Promise<Leadership> {
    const url = imageId
      ? `/api/admin/leadership/${leadershipId}/images/${imageId}`
      : `/api/admin/leadership/${leadershipId}/images`;

    return await apiFetch(url, {
      method: 'DELETE',
    });
  }
}
